// apps/web/src/lib/installPrompt.ts
//
// Store framework-free do prompt de instalação do PWA. O browser dispara
// `beforeinstallprompt` uma única vez (quando a página é instalável) — e pode
// dispará-lo ANTES de qualquer componente montar. Por isso o listener é
// registrado no boot (import side-effect em main.tsx, idempotente) e guarda o
// evento; os consumidores (InstallAppPrompt no login + item do menu do avatar)
// leem o estado via getInstallMode()/subscribe e chamam `promptInstall()`.
//
// `beforeinstallprompt` não existe na lib DOM do TS — declaramos o mínimo aqui.

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
  prompt(): Promise<void>;
}

export type InstallPromptState = {
  /** Há um prompt nativo em mãos e o app ainda não está instalado. */
  canInstall: boolean;
  /** App já roda em modo standalone (instalado). */
  installed: boolean;
};

let deferredPrompt: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  const displayModeStandalone = window.matchMedia?.('(display-mode: standalone)').matches ?? false;
  // iOS Safari expõe standalone fora do matchMedia padrão.
  const iosStandalone =
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
  return displayModeStandalone || iosStandalone;
}

let installed = isStandalone();

function emit(): void {
  listeners.forEach((listener) => listener());
}

/** Estado atual do prompt de instalação (snapshot). */
export function getInstallPromptState(): InstallPromptState {
  return { canInstall: deferredPrompt !== null && !installed, installed };
}

/** Assina mudanças de estado (retorna unsubscribe). */
export function subscribeInstallPrompt(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Dispara o prompt nativo de instalação. Retorna o desfecho, ou `'unavailable'`
 * se não há prompt em mãos (ex.: iOS, que nunca emite `beforeinstallprompt`).
 * O evento só pode ser usado uma vez — descartamos após o uso.
 */
export async function promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  const event = deferredPrompt;
  if (!event) return 'unavailable';
  deferredPrompt = null;
  emit();
  await event.prompt();
  const choice = await event.userChoice;
  return choice.outcome;
}

/**
 * Modo de instalação da plataforma atual:
 *  - `prompt`     — Chromium (desktop/Android): há prompt nativo em mãos → 1 clique.
 *  - `ios-safari` — iOS Safari, ainda não instalado: só instrução manual
 *                   (Compartilhar → "Adicionar à Tela de Início"; iOS nunca emite
 *                   `beforeinstallprompt`, e Chrome/Firefox/Edge no iOS não instalam).
 *  - `none`       — já instalado, ou plataforma sem caminho (iOS não-Safari,
 *                   Safari desktop, Firefox desktop).
 */
export type InstallMode = 'prompt' | 'ios-safari' | 'none';

function isIosDevice(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  // iPadOS 13+ se apresenta como "MacIntel" — desambiguar por touch.
  return navigator.platform === 'MacIntel' && (navigator.maxTouchPoints ?? 0) > 1;
}

/** iOS onde o A2HS funciona = Safari. CriOS/FxiOS/EdgiOS/OPiOS/GSA são não-Safari. */
function isIosSafari(): boolean {
  if (!isIosDevice()) return false;
  return !/CriOS|FxiOS|EdgiOS|OPiOS|OPT|GSA/.test(navigator.userAgent);
}

export function getInstallMode(): InstallMode {
  if (installed || isStandalone()) return 'none';
  if (deferredPrompt !== null) return 'prompt';
  if (isIosSafari()) return 'ios-safari';
  return 'none';
}

// Soneca do nudge de instalação (clique em "Agora não"): 7 dias em localStorage.
const INSTALL_SNOOZE_KEY = 'alamo_install_snooze_until';
const INSTALL_SNOOZE_MS = 7 * 24 * 60 * 60 * 1000;

export function snoozeInstallPrompt(): void {
  try {
    localStorage.setItem(INSTALL_SNOOZE_KEY, String(Date.now() + INSTALL_SNOOZE_MS));
  } catch {
    // localStorage indisponível — sem soneca (aceitável).
  }
}

export function isInstallPromptSnoozed(): boolean {
  try {
    const until = Number(localStorage.getItem(INSTALL_SNOOZE_KEY) ?? '0');
    return Number.isFinite(until) && Date.now() < until;
  } catch {
    return false;
  }
}

// Reset apenas para teste — o app importa o módulo uma vez e nunca reinicia.
export function __resetInstallPromptStateForTest(): void {
  deferredPrompt = null;
  installed = isStandalone();
  emit();
}

// Registro idempotente dos listeners de window (o módulo é side-effect e não
// deve empilhar handlers se for reimportado).
const REGISTERED_FLAG = '__alamoInstallPromptRegistered__';

if (typeof window !== 'undefined') {
  const win = window as typeof window & { [REGISTERED_FLAG]?: boolean };
  if (!win[REGISTERED_FLAG]) {
    win[REGISTERED_FLAG] = true;

    window.addEventListener('beforeinstallprompt', (event: Event) => {
      // Impede o mini-infobar automático do Chrome — a instalação passa a ser
      // deflagrada pelo nosso botão explícito "Instalar app".
      event.preventDefault();
      deferredPrompt = event as BeforeInstallPromptEvent;
      emit();
    });

    window.addEventListener('appinstalled', () => {
      installed = true;
      deferredPrompt = null;
      emit();
    });
  }
}
